import React, { useEffect, useRef } from "react";
import { Field, Select } from "@fluentui/react-components";
import { DialogBox } from "#root/components/DialogBox";
import { Tenant } from "#root/queries/tenants";

export interface TenantSelectionDialogProps {
  open: boolean;
  onOpenChange: () => void;
  title: string;
  primaryButtonText: string;
  onPrimaryButtonClick: () => void;
  secondaryButtonText: string;
  onSecondaryButtonClick: () => void;
  isPrimaryButtonLoading: boolean;
  isPrimaryButtonDisabled: boolean;
  isSecondaryButtonDisabled: boolean;
  selectedTenantId: string;
  setSelectedTenantId: (id: string) => void;
  tenants: Tenant[];
}

export const TenantSelectionDialog: React.FC<TenantSelectionDialogProps> = ({
  open,
  onOpenChange,
  title,
  primaryButtonText,
  onPrimaryButtonClick,
  secondaryButtonText,
  onSecondaryButtonClick,
  isPrimaryButtonLoading,
  isPrimaryButtonDisabled,
  isSecondaryButtonDisabled,
  selectedTenantId,
  setSelectedTenantId,
  tenants,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tenants && tenants.length > 0 && !selectedTenantId) {
      setSelectedTenantId(tenants[0].id || '');
    }
  }, [tenants, selectedTenantId, setSelectedTenantId]);

  useEffect(() => {
    if (open && containerRef.current) {
      containerRef.current.focus();
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (!isPrimaryButtonDisabled && !isPrimaryButtonLoading) {
        onPrimaryButtonClick();
      }
    }
  };

  return (
    <DialogBox
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      primaryButtonText={primaryButtonText}
      onPrimaryButtonClick={onPrimaryButtonClick}
      secondaryButtonText={secondaryButtonText}
      onSecondaryButtonClick={onSecondaryButtonClick}
      isPrimaryButtonLoading={isPrimaryButtonLoading}
      isPrimaryButtonDisabled={isPrimaryButtonDisabled}
      isSecondaryButtonDisabled={isSecondaryButtonDisabled}
    >
      <div
        ref={containerRef}
        tabIndex={0} 
        onKeyDown={handleKeyDown}
        style={{ outline: "none" }}
      >
        <Field label="Organization" required>
          <Select
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
          >
            {tenants?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.organizationName || "No name"}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </DialogBox>
  );
};

export default TenantSelectionDialog;
